import { prettifySalesCategory } from "api-types/builders/salesCategory";
import type { BuilderSalesCategoryCustomization } from "api-types/builders/salesCategoryTypes";
import type { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { arrayUnique } from "common/utils/arrays";
import { snakeCaseToTitleCase } from "common/utils/strings";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import SearchBar from "lib/components/builders/communities/plans/showroom/SearchBar";
import PlanLibraryBooleanOptionSetModal from "lib/components/builders/plan-library/modal/PlanLibraryBooleanOptionSetModal";
import PlanLibraryMassOverrideModal from "lib/components/builders/plan-library/modal/PlanLibraryMassOverrideModal";
import PlanLibraryNonBoolOptionSetModal from "lib/components/builders/plan-library/modal/PlanLibraryNonBoolOptionSetModal";
import PlanLibraryOptionModal from "lib/components/builders/plan-library/modal/PlanLibraryOptionModal";
import PlanLibraryOptionRuleModal from "lib/components/builders/plan-library/modal/PlanLibraryOptionRuleModal";
import PlanLibraryBooleanOptionSets from "lib/components/builders/plan-library/PlanLibraryBooleanOptionSets";
import {
  messageRelatedSalesCategories,
  salesCategoryMessages,
} from "lib/components/builders/plan-library/PlanLibraryMessages";
import PlanLibraryNonBoolOptionSets from "lib/components/builders/plan-library/PlanLibraryNonBoolOptionSets";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { PlanLibraryEditRuleButton } from "lib/components/builders/plan-library/PlanLibraryRuleBadge";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import Link from "ui/components/Link";
import Checkbox from "ui/forms/CheckboxOld";
import IconChevron from "ui/icons/IconChevron";
import IconCollapseAll from "ui/icons/IconCollapseAll";
import IconExpandAll from "ui/icons/IconExpandAll";
import IconTooltip from "ui/icons/IconTooltip";
import Tooltip from "ui/popovers/Tooltip";
import { Message } from "ui/text/Message";
import styles from "./PlanLibraryOptionSetsTab.module.scss";

function Header() {
  const { hideEmptySalesCategories, optionSetsCount, isReadOnly, planName } = usePlanLibrarySelector((state) => ({
    hideEmptySalesCategories: state.hideEmptySalesCategories,
    optionSetsCount: Object.values(state.primarySalesCategorySizes).reduce((a, v) => a + v, 0),
    isReadOnly: state.isReadOnly,
    planName: state.planName,
  }));

  const dispatch = usePlanLibraryDispatch();

  return (
    <SectionHeader
      sticky
      title={`Options: ${optionSetsCount}`}
      headerSearch={
        <SearchBar
          id="optionSetSearch"
          handleChange={(search) => dispatch(planLibraryActions.setSearch(search))}
          marginLeft={16}
        />
      }
      subtitle={`${isReadOnly ? "View" : "Manage"} option set overrides for ${planName}`}
      headerButton={
        <>
          {!isReadOnly && (
            <Button
              btnStyle="secondary"
              btnSize="medium"
              onClick={() => dispatch(planLibraryActions.enableMassEditModal(true))}
            >
              Mass Override
            </Button>
          )}
          <PlanLibraryEditRuleButton />
        </>
      }
      headerToggle={
        <div className={styles.hideEmptyCategoriesCheckbox}>
          <Checkbox
            checked={hideEmptySalesCategories}
            onChange={() => dispatch(planLibraryActions.setHideEmptySalesCategories(!hideEmptySalesCategories))}
          />
          Hide Empty Categories
        </div>
      }
      hr
    />
  );
}

function Modals() {
  const { modalOptionSet, modalOption, massEditing, editOptionRules } = usePlanLibrarySelector((state) => ({
    modalOptionSet: state.modalOptionSet,
    modalOption: state.modalOption,
    massEditing: state.massEditing,
    editOptionRules: state.editOptionRules,
  }));

  if (modalOptionSet && modalOption) {
    return <PlanLibraryOptionModal />;
  } else if (modalOptionSet?.isBooleanOptionSet === true) {
    return <PlanLibraryBooleanOptionSetModal />;
  } else if (modalOptionSet?.isBooleanOptionSet === false) {
    return <PlanLibraryNonBoolOptionSetModal />;
  } else if (massEditing) {
    return <PlanLibraryMassOverrideModal />;
  } else if (editOptionRules !== null) {
    return <PlanLibraryOptionRuleModal />;
  }
  return null;
}

interface ExpandCollapseTooltipProps {
  message: string;
  icon: ReactNode;
  onClick: () => void;
}

function ExpandCollapseTooltip({ message, icon, onClick }: ExpandCollapseTooltipProps) {
  return (
    <Tooltip.Root delayDuration={0}>
      <Tooltip.Trigger>
        <div className={styles.tooltip} onClick={onClick}>
          {icon}
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <div className={styles.tooltipMessageWrapper}>{message}</div>
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

function SalesCategoryTooltip({ category }: { category: string }) {
  if (!messageRelatedSalesCategories.includes(category as SalesCategory)) {
    return null;
  }
  return (
    <Tooltip.Root delayDuration={0}>
      <Tooltip.Trigger>
        <div>
          <IconTooltip color="stoneDark" className={styles.tooltip} size={18} />
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <div className={styles.tooltipMessageWrapper}>{salesCategoryMessages[category]}</div>
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

function PrimarySalesCategoryMenu() {
  const {
    primarySalesCategorySizes,
    primarySalesCategoryKeyToNameMap,
    secondaryToPrimarySalesCategoryMap,
    hideEmptySalesCategories,
  } = usePlanLibrarySelector((state) => ({
    primarySalesCategorySizes: state.primarySalesCategorySizes,
    primarySalesCategoryKeyToNameMap: state.primarySalesCategoryKeyToNameMap,
    secondaryToPrimarySalesCategoryMap: state.secondaryToPrimarySalesCategoryMap,
    hideEmptySalesCategories: state.hideEmptySalesCategories,
  }));

  // We need primary sales category names to be in the same order as they appear in the secondaryToPrimarySalesCategoryMap so that it matches the order of the sub categories components.
  // See PlanLibraryOptionSetsTab for where those are added.
  // The special categories should be included at the top.
  const uniquePrimarySalesCategoryKeys = arrayUnique(Object.values(secondaryToPrimarySalesCategoryMap));

  return (
    <nav className={`${styles.navigation} ${styles.sticky}`}>
      {uniquePrimarySalesCategoryKeys.map((key) => {
        const prettyName = primarySalesCategoryKeyToNameMap[key];

        function onClick() {
          const element: HTMLHtmlElement | null = document.querySelector(`h3#${key}`);
          if (element) {
            element.style.scrollMarginTop = "100px";
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        return (
          <div
            style={{ display: hideEmptySalesCategories && primarySalesCategorySizes[key] === 0 ? "none" : "" }}
            key={`${key}_nav_item`}
          >
            <Link
              className={key === "UNCATEGORIZED" ? `${styles.navItem} ${styles.uncategorized}` : styles.navItem}
              onClick={onClick}
              id={key}
            >
              {snakeCaseToTitleCase(prettyName)}
              {` (${primarySalesCategorySizes[key]})`}
            </Link>
          </div>
        );
      })}
    </nav>
  );
}

function NonBoolOptionSetsInSalesCategory({ category }: { category: string }) {
  const { show } = usePlanLibrarySelector((state) => ({
    show: state.nonBoolOptionSetsPerSalesCategory[category]?.length > 0,
  }));
  if (show) {
    return <PlanLibraryNonBoolOptionSets salesCategoryName={category} />;
  }
  return null;
}

function BooleanOptionSetsInSalesCategory({ category }: { category: string }) {
  const { show } = usePlanLibrarySelector((state) => ({
    show: state.booleanOptionSetsPerSalesCategory[category]?.length > 0,
  }));
  if (show) {
    return <PlanLibraryBooleanOptionSets salesCategoryName={category} />;
  }
  return null;
}

function SalesCategoryEmptyMessage({ category }: { category: string }) {
  const { empty } = usePlanLibrarySelector((state) => ({ empty: !state.salesCategorySizes[category] }));
  if (empty) {
    return (
      <div className={styles.emptyCategoryMessageWrapper}>
        <Message messageType="default">You have not added any options to this category yet.</Message>
      </div>
    );
  }
  return null;
}

export default function PlanLibraryOptionSetsTab() {
  const { hideEmptySalesCategories, salesCategorySizes, secondaryToPrimarySalesCategoryMap, customization } =
    usePlanLibrarySelector((state) => ({
      hideEmptySalesCategories: state.hideEmptySalesCategories,
      salesCategorySizes: state.salesCategorySizes,
      secondaryToPrimarySalesCategoryMap: state.secondaryToPrimarySalesCategoryMap,
      customization: state.builder?.salesConfig?.salesCategoryCustomization,
    }));

  // We want to always trigger a prop update when defaultState changes
  const [defaultState, setDefaultState] = useState<[boolean]>([true]);

  return (
    <>
      <Header />

      <Modals />

      <div className={styles.wrapper}>
        <div className={styles.expandCollapseContainer}>
          <ExpandCollapseTooltip
            message="Collapse All"
            icon={<IconCollapseAll />}
            onClick={() => {
              setDefaultState([false]);
            }}
          />
          <ExpandCollapseTooltip
            message="Expand All"
            icon={<IconExpandAll />}
            onClick={() => {
              setDefaultState([true]);
            }}
          />
        </div>

        <PrimarySalesCategoryMenu />

        <div className={styles.content}>
          {Object.keys(secondaryToPrimarySalesCategoryMap).map((secondaryCategoryName) => {
            const primaryCategoryKey = secondaryToPrimarySalesCategoryMap[secondaryCategoryName];
            if (primaryCategoryKey) {
              if (salesCategorySizes[secondaryCategoryName] > 0 || !hideEmptySalesCategories) {
                return (
                  <Category
                    key={secondaryCategoryName}
                    {...{ defaultState, categoryName: secondaryCategoryName, primaryCategoryKey, customization }}
                  />
                );
              }
            }

            return null;
          })}
        </div>
      </div>
    </>
  );
}

function Category({
  defaultState,
  categoryName,
  primaryCategoryKey,
  customization,
}: {
  defaultState: [boolean];
  categoryName: string;
  primaryCategoryKey: string;
  customization?: BuilderSalesCategoryCustomization;
}) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const [newDefault] = defaultState;
    if (newDefault && !open) {
      setOpen(true);
    }
    if (!newDefault && open) {
      setOpen(false);
    }
  }, [defaultState]);

  return (
    <details
      key={categoryName}
      onToggle={(e) => {
        setOpen(e.currentTarget.open);
      }}
      open={open}
    >
      <summary className={styles.categoryHeader}>
        <h3 className={styles.categoryName} id={primaryCategoryKey}>
          {prettifySalesCategory(categoryName, customization)}
        </h3>
        <SalesCategoryTooltip category={categoryName} />
        <IconChevron className={styles.detailsMarker} />
      </summary>

      <hr className={styles.categoryDivider} />

      <NonBoolOptionSetsInSalesCategory category={categoryName} />
      <BooleanOptionSetsInSalesCategory category={categoryName} />
      <SalesCategoryEmptyMessage category={categoryName} />

      <div className={styles.spacer} />
    </details>
  );
}
